"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import { fetchHeaderMenu } from "@/api-service/menu";
import { fetchAllCurrencies } from "@/api-service/currency";
import { Currency } from "@/packages/types/currency";
import { adaptMenuData, MenuNode } from "./menuAdapter";

interface HeaderCacheContextType {
  categories: MenuNode[];
  currencies: Currency[];
  loadingCategories: boolean;
  loadingCurrencies: boolean;
}

const HeaderCacheContext = createContext<HeaderCacheContextType | undefined>(undefined);

export function HeaderCacheProvider({ children }: { children: React.ReactNode }) {
  const [categories, setCategories] = useState<MenuNode[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);

  useEffect(() => {
    let active = true;

    const loadCategories = async () => {
      try {
        const res = await fetchHeaderMenu();
        console.log("Fetched header categories:", res.data);
        if (active) {
          const { nodes } = adaptMenuData(res.data);
          setCategories(nodes || []);
        }
      } catch (err) {
        console.error("Failed to cache header categories", err);
      } finally {
        if (active) setLoadingCategories(false);
      }
    };

    const loadCurrencies = async () => {
      try {
        const res = await fetchAllCurrencies();
        if (active) {
          setCurrencies(res.data || []);
        }
      } catch (err) {
        console.error("Failed to cache header currencies", err);
      } finally {
        if (active) setLoadingCurrencies(false);
      }
    };

    loadCategories();
    loadCurrencies();

    return () => {
      active = false;
    };
  }, []);

  return (
    <HeaderCacheContext.Provider
      value={{
        categories,
        currencies,
        loadingCategories,
        loadingCurrencies,
      }}
    >
      {children}
    </HeaderCacheContext.Provider>
  );
}

export function useHeaderCache() {
  const context = useContext(HeaderCacheContext);
  if (!context) {
    throw new Error("useHeaderCache must be used within a HeaderCacheProvider");
  }
  return context;
}
